import { getQueryParameter } from '../../../helpers/dataUrl';
import AppDecorate from '../../app/decorate';
import History from '../../core/history/history';
import { instance as CompanyInstance } from '../../factory/company';
import UserInfoFactory from '../../factory/user-info';
import cookieStorage from '../../utils/cookie-storage';
import humps from '../../utils/humps';
import Facade from '../abstract/facade';
import StickerGetter from '../design/getters/sticker';
import { logError } from 'src/utils/logger';

function EditorFacade () {
  this.isLoading = false;
  this.isClosing = false;
  this.subscribers = {};
  this.handlers = {
    distribute: require('./handlers/distribute'),
    align: require('./handlers/align'),
    background: require('./handlers/background'),
    cache: require('./handlers/cache'),
    coach_marks: require('./handlers/coach-marks'),
    confirm: require('./handlers/confirm'),
    download: require('./handlers/download'),
    header_and_footer: require('./handlers/header-and-footer'),
    history: require('./handlers/history'),
    image: require('./handlers/image'),
    page: require('./handlers/page'),
    printing: require('./handlers/printing'),
    share: require('./handlers/share'),
    shopping_cart: require('./handlers/shopping-cart'),
    table_of_contents: require('./handlers/table-of-contents'),
    tool_mode: require('./handlers/tool-mode'),
    translations: require('./handlers/translations'),
    video: require('./handlers/video'),
    zoom: require('./handlers/zoom'),
    printing_mode: require('./handlers/printing-mode'),
    snackbar: require('./handlers/snackbar')
  };

  this.getters = {
    header_and_footer: require('./getters/header_and_footer'),
    history: require('./getters/history'),
    log_info: require('./getters/logs'),
    page: require('./getters/page')
  };
}

EditorFacade.prototype = new Facade();
EditorFacade.prototype.constructor = EditorFacade;
EditorFacade.prototype.initialize = function (currentConfig) {
  Facade.prototype.initialize.call(this);
  this.handlers.zoom.curConfig = currentConfig;

  _.bindAll(this, 'get', 'close', 'deselectAll', '_close', '_doubleTap', 'initLoadingEvents', '_callSubscribers', 'listenIsLoading');

  this.tool = INKIVE.Tool.getInstance();
  this.editor = this.tool.editor;

  this.initSelectEvent();
  this.initLoadingEvents();
  this.editor.gesture.on('double_click', this._doubleTap);

  History.on('all', _.partial(this.propagate, 'history'));
};

EditorFacade.prototype.initSelectEvent = function () {
  const canvas = this.canvas;

  canvas.bind('editor.sub_selected', _.bind(function (c, subElements) {
    subElements = _.compact(subElements);

    if (!isSameSelection(this.subElements, subElements)) {
      this._select(subElements);
    }
  }, this));

  canvas.bind('editor.elem_selected', _.bind(function (c, currentElems) {
    this.subElements.length && this._select(_.compact(currentElems));
  }, this));

  canvas.bind('editor.selected', _.bind(function (c, currentElems) {
    currentElems = _.compact(currentElems);

    if (!isSameSelection(this.selectedElements, currentElems)) {
      canvas.runExtensions('selectedChanged', { elems: currentElems });
      this._select(currentElems);
    }
  }, this));

  function isSameSelection (selectedElements, currentElems) {
    return selectedElements && currentElems.length === selectedElements.length &&
      _.all(currentElems, function (element1) {
        return _.any(selectedElements, function (element2) {
          return element1.getAttribute('id') === element2.getAttribute('id') ||
          (element2.dataset.oldId && element1.querySelector(`#${element2.dataset.oldId}`));
        });
      });
  }
};

EditorFacade.prototype.initLoadingEvents = function () {
  const entity = 'loaderEvents';

  this.subscribers[entity] = this.subscribers[entity] || [];

  INKIVE.DesignFacade.on('svg_loader.show', eventHandler.call(this, true));
  INKIVE.DesignFacade.on('svg_loader.hide', eventHandler.call(this, false));

  function eventHandler (isLoading) {
    return () => {
      const shouldTriggerEvent = this.isLoading !== isLoading;
      if (!shouldTriggerEvent) return;

      this.isLoading = isLoading;

      return this._callSubscribers(entity, isLoading);
    };
  }
};

EditorFacade.prototype.get = function (elementType, attributeChain) {
  const functionName = humps.camelize('get_' + elementType);

  if (this[functionName]) {
    return this[functionName](attributeChain);
  }

  if (this.getters[elementType]) {
    return this.getters[elementType].get.apply(
      this.getters[elementType], _.rest(arguments)
    );
  }
};

EditorFacade.prototype.select = function (elementIds) {
  elementIds = _.isArray(elementIds) ? elementIds : [elementIds];
  const subelements = _.chain(elementIds)
    .map(function (id) { return $('#' + id)[0]; })
    .filter(isSubelement)
    .value();

  const elements = _.map(elementIds, function (id) {
    if (id === 'background') {
      return $('#svgcontent')
        .children('g.background')
        .children(':not(title)')
        .last()[0];
    }

    const $element = $('#' + id);

    if (isSubelement($element[0])) {
      return $element.parents('.element')[0];
    }

    return $element[0];
  });

  this.canvas.selectOnly(elements);

  if (subelements.length > 0) {
    _.each(subelements, function (element) { $(element).addClass('active'); });
    this._select(subelements);
  }

  function isSubelement (element) {
    return $(element).closest('.subelement').length;
  }
};

EditorFacade.prototype.deselectAll = function () {
  this.subElements = [];
  this.selectedElements = [];
  this.canvas.clearSelection();
  this.trigger('deselect_all');
};

EditorFacade.prototype.nameChange = async function (parameters) {
  INKIVE.C.BOOK.name = Base64.encode(parameters.name);
  this.isSavingProjectName = true;

  parameters.name && CompanyInstance.setProjectData(parameters).then((data) => {
    data.name = parameters.name;
    this.trigger('project.name.saved', parameters.name);
    try {
      // Save the page once the project name is changed
      INKIVE.EditorFacade.trigger('page.save_from_keyboard', {});
    } catch {
      // TODO
    }
    parameters.callback && parameters.callback();
    this.isSavingProjectName = false;
  }, (error) => {
    INKIVE.C.DEBUG && console.error('Error saving project name. Error: ' + error);
    this.isSavingProjectName = false;
  });
};

EditorFacade.prototype.closeCropIfActive = function () {
  const currentLayer = this.canvas.getCurrentDrawing().getCurrentLayer();
  const isClippingImage = currentLayer && currentLayer.getAttribute('class') === 'clipperImage';

  if (isClippingImage) {
    const isBackgroundCrop = !!currentLayer.querySelector('.layoutClipperImage.background');
    this.closeCrop(isBackgroundCrop ? 'background' : 'image');
  }

  return isClippingImage;
};

EditorFacade.prototype.closeCrop = function (elementIds) {
  const $layoutClipperImage = $('.clipperImage .layoutClipperImage');

  this.canvas.runExtensions('cropLayoutContent', {
    elem: $layoutClipperImage[0],
    layout: $('#' + $layoutClipperImage.data('old-id'))[0],
    hasChanges: true
  });

  this.trigger((elementIds === 'background' ? 'background' : 'image') + '.crop.end');
};

EditorFacade.prototype.cancelCrop = function (elementIds) {
  const $layoutClipperImage = $('.clipperImage .layoutClipperImage');

  this.canvas.runExtensions('cropLayoutContent', {
    elem: $layoutClipperImage[0],
    layout: $('#' + $layoutClipperImage.data('old-id'))[0],
    hasChanges: false
  });

  this.trigger((elementIds === 'background' ? 'background' : 'image') + '.crop.cancel');
};

EditorFacade.prototype.close = function (parameters) {
  if (this.isClosing) return;
  this.isBackToCreate = parameters && parameters.back_to_create;

  this.closeCropIfActive();

  const svgCont = this.editor.getSvgString();
  const projectName = Base64.decode(INKIVE.C.BOOK.name).trim().toLowerCase();
  const isBrandProject = INKIVE.Helpers.projectIsLinkedToRestrictions();

  if (this.isSavingProjectName) {
    this.off('project.name.saved').once('project.name.saved', this.close);
  } else if (!isBrandProject && !INKIVE.EditorModeManager.isMode('logo') &&
    !INKIVE.C.IS_MOBILE_APP && (projectName === 'new project' || projectName === '')) {
    $('body > header [data-value="project-name"] span').text('').focus();
    this.canvas.call('advice.show', 'project-name');
  } else if (INKIVE.C.IS_MOBILE_APP) {
    this.isClosing = true;
    const saveBeforeClose = !INKIVE.Helpers.areEqual(svgCont, this.handle('cache', 'get'));

    saveBeforeClose || this._close();
    saveBeforeClose && this.handlers.page.save()
      .then(this._close, _.bind(function () { this.isClosing = false; }, this));
  } else {
    let modal;
    const saveAndclose = _.bind(function () {
      this.isClosing = true;
      INKIVE.Modal.togglePageLoader(true);
      INKIVE.disableWindowExitConfirm = true;

      modal && modal.close();
      this.handlers.page._getChangesToSave(parameters).then(_.bind(function (changes) {
        changes || this._close();
        changes && this.handlers.page.save(parameters, changes)
          .then(this._close, _.bind(function () {
            this.isClosing = false;
          }, this));
      }, this), () => {
        INKIVE.Modal.togglePageLoader(false);
        this.isClosing = false;
      });
    }, this);

    const closeWithoutSaving = _.bind(function () {
      $('.modal').hide();
      INKIVE.Modal.togglePageLoader(true);
      this._close();
    }, this);

    let innerTextKey;

    if ($('#ik-download-notification').is(':visible')) {
      innerTextKey = 'modal.close_confirm_dialog_downloading';
    } else if (INKIVE.hasEditorUnsavedChanges && !INKIVE.C.IS_AUTO_SAVE) {
      innerTextKey = 'modal.close_confirm_dialog';
    }

    if (innerTextKey) {
      modal = INKIVE.Modal.modalWindow(
        INKIVE.TranslateHelper.translate(innerTextKey),
        {
          headerTitle: INKIVE.TranslateHelper.translate('general.close'),
          width: 350,
          buttons: [
            {
              title: INKIVE.TranslateHelper.translate('general.dont_save'),
              onClick: closeWithoutSaving,
              width: '150px'
            },
            {
              title: INKIVE.TranslateHelper.translate('general.save'),
              onClick: saveAndclose,
              width: '150px'
            }
          ]
        }
      );
    } else {
      if (INKIVE.C.IS_AUTO_SAVE) {
        saveAndclose();
      } else {
        closeWithoutSaving();
      }
    }
  }
};

EditorFacade.prototype.convertToImageBox = function () {
  const element = this.canvas.getSelectedElems()[0];
  const clipPathBB = this.canvas.getBBox(element);
  const removeAttributes = ['stroke', 'stroke-width', 'stroke-dasharray', 'stroke-linejoin', 'stroke-linecap', 'stroke-opacity', 'fill'];
  const rect = this.canvas.copyElem(element);
  const isBackground = $(element).closest('g.background').length;

  if (!isBackground) {
    _.each(removeAttributes, rect.removeAttribute, rect);
    rect.setAttribute('fill-opacity', '1');
    this.canvas.deleteSelectedElements();
    rect.removeAttribute('id');
  }

  this.canvas.runExtensions('addLayoutBox', {
    elem: rect,
    clipPathBB,
    background: isBackground
  });

  INKIVE.Tool.autoSave(rect);
  this.trigger('convert_to_image_box');
};

EditorFacade.prototype.getOrderedSelection = function () {
  return this.canvaSelectedElementsOrdered;
};

EditorFacade.prototype.getSelection = function () {
  const designFacade = require('../design/design');
  const restrictionsFacade = require('../restrictions/restrictions');

  this.subElements = [];

  return _.compact(_.map(this.canvaSelectedElements, _.bind(function (element) {
    let innerElement;

    if (element) {
      if ($(element).is('.subelement')) {
        const subElement = element;
        element = $(element).parents('.element')[0];
        innerElement = getInnerElement.call(this, element, subElement);
        // Ignore subElement if it belongs to a table
        element.classList.contains('table') || this.subElements.push(subElement);
      }

      let handledElementId = $(element).is('.background') ? 'background' : element.getAttribute('id');
      const oldId = $(element).attr('data-old-id');

      if (oldId && $('#' + oldId).length > 0) {
        handledElementId = $(element).attr('data-old-id');
      }

      const data = designFacade.get(handledElementId);

      return _.extend(data,
        { permissions: (data ? restrictionsFacade.getAllElementPermissions(data.id) : []) },
        { inner_element: innerElement }
      );
    }
  }, this)));

  function getInnerElement (element, subElement) {
    let innerElement;
    const superElementType = this.getType(element);

    if (superElementType === 'sticker') {
      const stickerGetter = new StickerGetter();
      stickerGetter.element = element;
      _.any(stickerGetter.getTexts(), function (textGetter, index) {
        if (textGetter.element === subElement) {
          innerElement = {
            type: 'text',
            index
          };
          return innerElement;
        }
      });
    }

    return innerElement;
  }
};

EditorFacade.prototype.setWorkareaBackgroundColor = function (parameters) {
  parameters = parameters || {};
  const color = _.contains(parameters.color, '#') ? parameters.color : '#' + parameters.color;

  $('#canvas-panel').css('background-color', color);
  this.trigger('set_workarea_background_color', color);
};

EditorFacade.prototype.align = function (parameters) {
  this.handlers.align.handle(parameters);
};

EditorFacade.prototype.tabState = function () {
  const panel = INKIVE.Helpers.getActiveTab();

  if (panel) {
    return {
      panel,
      tab: $('#tab' + humps.pascalize(panel)).find('.header-links').find('.active').attr('data-key')
    };
  }
};

EditorFacade.prototype.switchTab = function (parameters) {
  const panel = humps.pascalize(parameters.panel);

  $('#tool-panel').find('[href=#tab' + panel + ']').trigger('click');
  parameters.tab && _.defer(function () {
    $('#tab' + panel).find('.header-links').find('[data-key=' + parameters.tab + ']').trigger('click');
  });
};

EditorFacade.prototype._close = async function () {
  INKIVE.hasEditorUnsavedChanges = false;

  const isExportFlow = !INKIVE.C.IS_MOBILE_APP &&
    !INKIVE.EditorModeManager.isMode('brand_template') &&
    !INKIVE.EditorModeManager.isMode('restrict') &&
    getQueryParameter('external_ref')
  ;

  const renderPromise = isExportFlow
    ? new Promise(resolve => INKIVE.Helpers.DownloadProcedure.download(resolve))
    : null;

  await new Promise(resolve => INKIVE.Helpers.api(
    'inkive/close', 'GET', { scrapbook: INKIVE.C.BOOK.id }, resolve
  ));

  if (isExportFlow) {
    const render = await renderPromise;
    await this._displayExternalPlatformNavigationModal(render);
  }

  window.location = this._getMobileAppExitUrl() ||
    this._getExternalExitUrl() ||
    this._getReferralExitUrl() ||
    this._getDefaultExitUrl()
  ;
};

EditorFacade.prototype._getMobileAppExitUrl = function () {
  return INKIVE.C.IS_MOBILE_APP && `${INKIVE.C.BASE_URL}create/designs/`;
};

EditorFacade.prototype._getExternalExitUrl = function () {
  const backUrl = getQueryParameter('back_url');

  if (!this.isBackToCreate && backUrl && !INKIVE.EditorModeManager.isMode('brand_template')) {
    let exitUrl = backUrl;
    exitUrl += /.*\?[^/]*$/.test(exitUrl) ? '&' : '?';
    exitUrl += `design_id=${INKIVE.C.BOOK.id}`;

    if (INKIVE.C.BOOK.external_reference) {
      exitUrl += `&design_external_reference=${INKIVE.C.BOOK.external_reference}`;
    }

    return exitUrl;
  }
};

EditorFacade.prototype._getReferralExitUrl = function () {
  const referrer = getQueryParameter('referrer');
  return referrer && `${this._getDefaultExitDomain()}${referrer}`;
};

EditorFacade.prototype._getDefaultExitUrl = function () {
  return `${this._getDefaultExitDomain()}${this._getDefaultExitPath()}`;
};

EditorFacade.prototype._getDefaultExitDomain = function () {
  const baseDomain = INKIVE.C.IS_WEBRAND ? INKIVE.C.WE_BRAND : INKIVE.C.BASE_URL;
  const whiteLabelConfig = INKIVE.C.WHITE_LABEL_CONFIGURATION || {};
  const domain = whiteLabelConfig.domain || CONFIG.company_domain || cookieStorage.get('company_domain');
  const shouldGoBackToCompany = INKIVE.EditorModeManager.isMode('restrict') ||
    (INKIVE.EditorModeManager.isMode('brand_template') && domain !== 'desygner') ||
    (domain !== 'desygner' && domain !== 'wattpad-master');

  if (shouldGoBackToCompany) {
    const referrer = getQueryParameter('referrer');
    const shouldUseParentDomain = whiteLabelConfig.parent_company &&
      referrer?.includes('workspace/')
    ;

    return shouldUseParentDomain
      ? baseDomain.replace(/(https?:\/\/)/, `$1${whiteLabelConfig.parent_company}.`)
      : baseDomain.replace(/(https?:\/\/)/, `$1${domain}.`);
  }

  return baseDomain;
};

EditorFacade.prototype._getDefaultExitPath = function () {
  return INKIVE.EditorModeManager.isMode('brand_template')
    ? 'create/templates/'
    : 'create/designs/';
};

EditorFacade.prototype._displayExternalPlatformNavigationModal = function (render) {
  return new Promise(resolve => {
    if (render) {
      INKIVE.Modal.closeAllPageLoaders();
      INKIVE.Modal.alertMessage(INKIVE.TemplatesHelper('alert_span', {
        text: INKIVE.TranslateHelper.translate('top_submenu.share.project_ready')
      }), {
        skip: false,
        callback: [function () {
          const externalUrl = 'https://www.wattpad.com/myworks/covers/upload?source=desygner' +
            `&storyId=${getQueryParameter('external_ref')}` +
            `&uri=${encodeURIComponent(render)}`;

          window.open(externalUrl, '_blank');
          resolve();
        }]
      });
    } else {
      resolve();
    }
  });
};

EditorFacade.prototype._select = function (elems) {
  // If selection contains a subelement that belongs to a table, use table itself instead of that subelement
  this.canvaSelectedElementsOrdered = elems.map(element => {
    const $element = $(element);

    if ($element.is('.subelement')) {
      const $parentAsTable = $element.parents('.table');
      if ($parentAsTable) return $parentAsTable[0];
    }

    return element;
  });

  this.canvaSelectedElements = _.sortBy(elems, function (e) {
    return -$(e).index();
  });

  this.selectedElements = _.compact(_.map(elems, function (element) {
    return $(element).is('.subelement') ? $(element).parents('.element')[0] : element;
  }));

  this.trigger('select', this.getSelection());
};

EditorFacade.prototype._doubleTap = function (event) {
  const designFacade = require('../design/design');
  const elems = this.canvas.getSelectedElems();
  const restrictionsFacade = require('../restrictions/restrictions');

  this.trigger('double_tap', _.map(elems, _.bind(function (element) {
    let innerElement;
    const type = this.getType(element);

    if (type === 'sticker') {
      const textArea = $(event.target).closest('g.textarea')[0];
      if (textArea) {
        const stickerGetter = new StickerGetter();
        stickerGetter.element = element;
        _.any(stickerGetter.getTexts(), function (textGetter, index) {
          if (textGetter.element === textArea) {
            innerElement = {
              type: 'text',
              index
            };

            return innerElement;
          }
        });
      }
    }

    const id = type === 'imageclipper' ? element.dataset.oldId : element.getAttribute('id');
    const data = designFacade.get($(element).is('.background') ? 'background' : id);

    return _.extend(
      data,
      { permissions: restrictionsFacade.getAllElementPermissions(data.id) },
      { inner_element: innerElement }
    );
  }, this)));
};

EditorFacade.prototype.initializeUserCredentials = () => {
  CONFIG.credentials.hash = cookieStorage.get('user_hash');
  CONFIG.credentials.token = cookieStorage.get('user_token');

  const forceReload = true;

  UserInfoFactory.get(forceReload)
    .then(() => {
      INKIVE.PageFactory.updateCredentials(CONFIG.credentials.api_key);
      return INKIVE.EditorFacade.handle('page', 'save', { quiet: true });
    }, error => {
      logError('initializeUserCredentials', error);
    });
};

EditorFacade.prototype._callSubscribers = function (type, ...args) {
  const callbacks = this.subscribers[type] || [];

  return callbacks.forEach(callback => callback.apply(null, args));
};

EditorFacade.prototype.listenIsLoading = function (callback) {
  if (typeof callback !== 'function') return;

  this.subscribers.loaderEvents.push(callback);
};

module.exports = AppDecorate(require('./app/editor'), new EditorFacade());
